// TinyMCEEditor.jsx
import React from "react";
import { Editor } from "@tinymce/tinymce-react";
import axiosInstance from "../utils/axios";

const TinyMCEEditor = ({ value, onChange, onBlur, height = 420 }) => {
  return (
    <Editor
      tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
      apiKey="no-api-key"
      value={value}
      onEditorChange={(content) => onChange?.(content)}
      onBlur={onBlur}
      init={{
        license_key: "gpl",
        height,
        menubar: false,
        branding: false,
        toolbar_mode: "sliding",
        plugins: "lists link image video table code autoresize searchreplace preview",
        toolbar:
          "undo redo | blocks | bold italic underline | " +
          "alignleft aligncenter alignright | bullist numlist outdent indent | " +
          "link image customvideo table | searchreplace preview | code",

        image_advtab: true,
        image_caption: true,
        image_class_list: [
          { title: "None", value: "" },
          { title: "Align left", value: "align-left" },
          { title: "Align center", value: "align-center" },
          { title: "Align right", value: "align-right" },
        ],

        // Video configuration
        video_advtab: true,
        video_caption: true,
        video_class_list: [
          { title: "None", value: "" },
          { title: "Responsive", value: "responsive" },
          { title: "Full width", value: "full-width" },
        ],

        // Enable upload via button, paste, and drag/drop
        automatic_uploads: true,
        file_picker_types: "image video",
        paste_data_images: true,
        paste_data_videos: true,

        // Toolbar "image" -> open file dialog -> upload -> insert URL
        file_picker_callback: (cb, value, meta) => {
          const input = document.createElement("input");
          input.type = "file";
          
          // Set accept based on whether user clicked image or video button
          if (meta.filetype === "image") {
            input.accept = "image/*";
          } else if (meta.filetype === "video") {
            input.accept = "video/*";
          }
          
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            const fd = new FormData();
            fd.append("file", file, file.name);
            try {
              const res = await axiosInstance.post("/api/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
              const url = res?.data?.url || res?.data?.location;
              if (!url) throw new Error("No URL in upload response");
              cb(url, { title: file.name });
            } catch (e) {
              console.error("File upload failed:", e);
            }
          };
          input.click();
        },

        // Paste/drag-drop upload handler for images
        images_upload_handler: async (blobInfo, progress) => {
          const fd = new FormData();
          fd.append("file", blobInfo.blob(), blobInfo.filename());
          const res = await axiosInstance.post("/api/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              if (e.total) progress(Math.round((e.loaded / e.total) * 100));
            },
          });
          const url = res?.data?.url || res?.data?.location;
          if (!url) throw new Error("No URL in upload response");
          return url;
        },

        // Paste/drag-drop upload handler for videos
        videos_upload_handler: async (blobInfo, progress) => {
          const fd = new FormData();
          fd.append("file", blobInfo.blob(), blobInfo.filename());
          const res = await axiosInstance.post("/api/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              if (e.total) progress(Math.round((e.loaded / e.total) * 100));
            },
          });
          const url = res?.data?.url || res?.data?.location;
          if (!url) throw new Error("No URL in upload response");
          return url;
        },

        content_style: `
          body { font-family: -apple-system, Inter, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size:14px }
          img.align-left { float: left; margin: 0 1em 1em 0; }
          img.align-right { float: right; margin: 0 0 1em 1em; }
          img.align-center { display: block; margin-left: auto; margin-right: auto; }
          figure.image { display: inline-block; }
          figure.image.align-center { display: block; text-align: center; }
        `,

        // Add custom video button
        setup: (editor) => {
          // Add custom video button after image button
          editor.ui.registry.addButton('customvideo', {
            text: 'Video',
            icon: 'video',
            tooltip: 'Insert/Edit Video',
            onAction: () => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "video/*";
              input.onchange = async () => {
                const file = input.files?.[0];
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file, file.name);
                try {
                  const res = await axiosInstance.post("/api/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  const url = res?.data?.url || res?.data?.location;
                  if (!url) throw new Error("No URL in upload response");
                  
                  // Insert video element
                  editor.insertContent(`<video controls width="100%"><source src="${url}" type="${file.type}">Your browser does not support the video tag.</video>`);
                } catch (e) {
                  console.error("Video upload failed:", e);
                }
              };
              input.click();
            }
          });
        },
      }}
    />
  );
};

export default TinyMCEEditor;
